import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { motion } from 'motion/react';
import { chatMessages, contacts as fixtureContacts } from '../fixtures/uiKitFixtures';
import { snappyTween, staggerEntrance } from './motionUi';
import { postGrokRequest, patchUserState } from '../gameSessionApi';
import { buildGrokChatRequestBody } from '../buildGrokChatPayload';
import { loadChatMessages, saveChatMessages } from '../gameChatStorage';
import {
  applyRpDeltaToCharactersAndSets,
  buildRelationshipRewardDispatchDetail
} from '../relationshipRpApply';
import GamePreviewShell from './GamePreviewShell';
import RelationshipBadge from './RelationshipBadge';
import RpDeltaFloat from './RpDeltaFloat';
import { relationshipTierIndex, relationshipTierLabel, nextRpMilestone } from '../relationshipLevels';
import { replacePlayerNamePlaceholder } from '../chatTextPlaceholders';

/** Как в {@link GameCharacterAlbumsPanel} — градиенты для обложки без картинки */
const ALBUM_COVER_GRADIENTS = [
  'from-game-popup via-game-purpleMid to-game-banner',
  'from-game-menuTop via-game-popup to-game-purpleDeep',
  'from-game-purpleMid to-game-banner',
  'from-game-popup to-game-menuTop'
];

function albumCoverGradientIndex(seed) {
  const s = String(seed || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i) * (i + 1)) % ALBUM_COVER_GRADIENTS.length;
  return h;
}

function giftIconIsImageUrl(icon) {
  const s = String(icon || '').trim();
  return (
    s.startsWith('http://') ||
    s.startsWith('https://') ||
    s.startsWith('/') ||
    s.startsWith('data:')
  );
}

/** Маркер для промпта: модель видит, что карточка альбома уже показана (раньше album не шёл в историю → ИИ дублировал suggested_set_id). */
function albumOfferHistoryLine(characterName, msg) {
  const cn = String(characterName || '').trim() || 'Character';
  const sid = String(msg?.setId || '').trim();
  const title = String(msg?.title || 'Album').trim().replace(/"/g, "'");
  return `${cn}: [Chat UI: photo album offered; set_id=${sid}; title="${title}"]`;
}

function messagesToHistoryLines(messages, playerName, characterName) {
  const nick = String(playerName || '').trim();
  const cn = String(characterName || '').trim() || 'Character';
  const lines = [];
  for (const m of messages) {
    if (m.role === 'system') continue;
    if (m.type === 'album' && m.role === 'character') {
      lines.push(albumOfferHistoryLine(cn, m));
      continue;
    }
    if (m.type !== 'text') continue;
    const lineText = replacePlayerNamePlaceholder(m.text, nick);
    if (m.role === 'user') lines.push(`${nick}: ${lineText}`);
    else if (m.role === 'character') lines.push(`${cn}: ${lineText}`);
  }
  return lines;
}

function albumMsgOwned(msg, albumOwnedSetIds) {
  if (msg?.type !== 'album') return false;
  const sid = String(msg.setId || '').trim();
  if (sid && albumOwnedSetIds instanceof Set) return albumOwnedSetIds.has(sid);
  return Boolean(msg.purchased);
}

function MessageBubble({ msg, albumOwnedSetIds, onAlbumUnlock, onAlbumOpen, playerNickname = '' }) {
  const isUser = msg.role === 'user';
  const base = isUser ? 'ml-auto bg-[rgba(97,0,255,0.2)] text-game-usermsg' : 'mr-auto bg-[rgba(150,35,97,0.2)] text-game-body';

  if (msg.type === 'text') {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${base}`}
      >
        {replacePlayerNamePlaceholder(msg.text, playerNickname)}
      </motion.div>
    );
  }

  if (msg.type === 'error') {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-[85%] rounded-2xl border border-red-400/40 bg-red-950/40 px-3 py-2 text-sm text-red-100"
      >
        {replacePlayerNamePlaceholder(msg.text, playerNickname)}
      </motion.div>
    );
  }

  if (msg.type === 'image') {
    const caption = msg.caption || 'Image';
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`mr-auto max-w-[70%] overflow-hidden rounded-2xl border border-white/10 ${base}`}
      >
        <div className="flex aspect-video w-full flex-col items-center justify-center gap-1 bg-gradient-to-br from-game-purpleDeep to-game-menuTop px-4 text-center">
          <span className="text-2xl opacity-90" aria-hidden>
            🖼
          </span>
          <span className="text-xs font-medium text-game-text">{caption}</span>
        </div>
      </motion.div>
    );
  }

  if (msg.type === 'album') {
    const shots = msg.photoCount;
    const owned = albumMsgOwned(msg, albumOwnedSetIds);
    const setId = String(msg.setId || '').trim();
    const priceNum = Number(msg.price);
    const price = Number.isFinite(priceNum) ? priceNum : 0;
    const cover = String(msg.coverImageUrl || '').trim();
    const gradIdx = albumCoverGradientIndex(setId || msg.id);
    const photoLine =
      typeof shots === 'number' && Number.isFinite(shots) ? `${shots} photos` : null;

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="mr-auto w-full max-w-[11.25rem] overflow-hidden rounded-2xl border border-white/10 bg-game-surface text-left shadow-lg sm:max-w-[12.5rem]"
      >
        <div className="relative aspect-square w-full shrink-0 overflow-hidden">
          {cover ? (
            <img
              src={cover}
              alt=""
              className="h-full w-full object-cover"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : (
            <div
              className={`h-full w-full bg-gradient-to-br ${ALBUM_COVER_GRADIENTS[gradIdx]}`}
            />
          )}
          {owned ? (
            <span className="pointer-events-none absolute left-2 top-2 rounded-md bg-black/55 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              Library
            </span>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/50 px-2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-white/80">
                Locked
              </span>
              <span className="text-sm font-bold tabular-nums text-game-accent">{price} ◆</span>
            </div>
          )}
        </div>
        <div className="flex flex-1 flex-col p-2.5 sm:p-3">
          <p className="line-clamp-2 text-xs font-semibold leading-snug text-game-text sm:text-[13px]">
            {msg.title}
          </p>
          {photoLine ? (
            <p className="mt-1 text-[10px] text-game-muted sm:text-[11px]">{photoLine}</p>
          ) : null}
          {owned ? (
            <motion.button
              type="button"
              whileTap={{ scale: setId && onAlbumOpen ? 0.99 : 1 }}
              disabled={!setId || !onAlbumOpen}
              onClick={() => setId && onAlbumOpen?.(setId)}
              className="mt-3 w-full rounded-xl border border-white/15 bg-transparent py-2.5 text-center text-[11px] font-bold text-game-text sm:text-xs disabled:cursor-not-allowed disabled:opacity-40"
            >
              Open
            </motion.button>
          ) : (
            <motion.button
              type="button"
              whileTap={{ scale: setId && onAlbumUnlock ? 0.99 : 1 }}
              disabled={!setId || !onAlbumUnlock}
              onClick={() =>
                setId &&
                onAlbumUnlock?.({
                  id: setId,
                  title: msg.title,
                  photoCount: shots,
                  coverImageUrl: cover,
                  owned: false,
                  price
                })
              }
              className={`mt-3 w-full rounded-xl py-2.5 text-center text-[11px] font-bold sm:text-xs ${
                setId && onAlbumUnlock
                  ? 'bg-game-accent text-game-accentText'
                  : 'cursor-not-allowed bg-game-muted/25 text-game-muted'
              }`}
            >
              Unlock
            </motion.button>
          )}
        </div>
      </motion.div>
    );
  }

  return null;
}

function getCharDoc(catalog, characterId) {
  if (!characterId || !catalog?.length) return null;
  return catalog.find((c) => String(c._id) === String(characterId)) || null;
}

function albumRowFromSetCatalog(setsCatalog, setId, characterId) {
  const sid = String(setId || '').trim();
  const cid = String(characterId || '');
  if (!sid || !cid || !Array.isArray(setsCatalog)) return null;
  const s = setsCatalog.find((x) => String(x._id) === sid);
  if (!s) return null;
  const sc = String(s.characterId?._id || s.characterId);
  if (sc !== cid) return null;
  return {
    setId: sid,
    title: (s.name || 'Album').trim() || 'Album',
    photoCount: Array.isArray(s.photos) ? s.photos.length : 0,
    coverImageUrl: typeof s.coverImage === 'string' ? s.coverImage.trim() : '',
    price: Number(s.price) || 0
  };
}

/**
 * @param {boolean} [props.chatUiVisible] — вкладка чата открыта (шапка видна)
 * @param {string|null} [props.viewedCharacterId] — выбранный контакт
 * @param {(p: { characterId: string, characterName: string, preview: string }) => void} [props.onChatReplyNotification]
 * @param {(delta: number) => void} [props.onPendingAiDelta] — +1 старт запроса, −1 в finally
 * @param {Array} [props.setsCatalog] — каталог сетов (обложка, цена) для suggested_set_id
 * @param {(album: { id: string, title?: string, photoCount?: number, coverImageUrl?: string, owned?: boolean, price?: number }) => void} [props.onChatAlbumUnlock]
 * @param {(setId: string) => void} [props.onChatAlbumOpen] — открыть просмотр альбома
 * @param {{ kind: 'after_control'|'after_joi', characterId: string, sessionHistory?: string, completedTasks?: string, refusedTasks?: string }|null} [props.sessionFollowUpRequest]
 * @param {() => void} [props.onSessionFollowUpConsumed] — сбросить запрос после старта обработки
 */
export default function GameChatPanel({
  gameContacts,
  gameSelectedId,
  onGameSelectContact,
  embedded = false,
  onClose,
  chatUiVisible = true,
  viewedCharacterId = null,
  sessionFollowUpRequest = null,
  onSessionFollowUpConsumed,
  onChatReplyNotification,
  onPendingAiDelta,
  playerId = '',
  playerNickname = '',
  session = null,
  gameSettings = {},
  kinksCatalog = [],
  charactersCatalog = [],
  setsCatalog = [],
  giftCatalog = null,
  onUserStateUpdated,
  onInsufficientTokens,
  onChatAlbumUnlock,
  onChatAlbumOpen
}) {
  const gameMode = Array.isArray(gameContacts) && gameContacts.length > 0;
  const liveAi = Boolean(
    gameMode &&
      playerId &&
      String(playerNickname || '').trim() &&
      typeof onUserStateUpdated === 'function'
  );

  /** @type {Record<string, Array>} */
  const [messagesByChar, setMessagesByChar] = useState({});
  /** @type {Record<string, number>} */
  const [pendingByChar, setPendingByChar] = useState({});
  const [fixtureMessages, setFixtureMessages] = useState(() => [...chatMessages]);
  const [draft, setDraft] = useState('');
  const [giftPickerOpen, setGiftPickerOpen] = useState(false);
  const [chatRpDelta, setChatRpDelta] = useState({ delta: 0, seq: 0 });
  const scrollRef = useRef(null);
  const mountedRef = useRef(true);
  /** Защита от повторной обработки того же follow-up (Strict Mode / лишние эффекты). */
  const lastHandledFollowUpSeqRef = useRef(0);
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const onRpChange = (e) => {
      const d = e.detail || {};
      const delta = Number(d.delta);
      if (Number.isFinite(delta) && delta !== 0) {
        setChatRpDelta((prev) => ({ delta, seq: prev.seq + 1 }));
      }
    };
    window.addEventListener('game-flow-relationship-up', onRpChange);
    return () => window.removeEventListener('game-flow-relationship-up', onRpChange);
  }, []);

  const selectedGame = useMemo(() => {
    if (!gameMode) return null;
    const id = gameSelectedId || gameContacts[0]?.id;
    return gameContacts.find((c) => String(c.id) === String(id)) || gameContacts[0] || null;
  }, [gameMode, gameContacts, gameSelectedId]);

  const selectedId = selectedGame?.id != null ? String(selectedGame.id) : '';

  const characterDoc = useMemo(() => {
    if (!selectedId) return null;
    return getCharDoc(charactersCatalog, selectedId);
  }, [selectedId, charactersCatalog]);

  const messages = useMemo(() => {
    if (!liveAi || !selectedId) return [];
    const row = messagesByChar[selectedId];
    if (row !== undefined) return row;
    return loadChatMessages(playerId, selectedId);
  }, [liveAi, selectedId, messagesByChar, playerId]);

  useEffect(() => {
    if (!liveAi || !selectedId) return;
    setMessagesByChar((prev) => {
      if (prev[selectedId] !== undefined) return prev;
      return { ...prev, [selectedId]: loadChatMessages(playerId, selectedId) };
    });
  }, [liveAi, playerId, selectedId]);

  const chatLocked = Boolean(
    gameMode &&
      selectedGame &&
      typeof selectedGame.sessionNumber === 'number' &&
      Number(selectedGame.sessionNumber) < 1
  );

  const pendingForSelected = pendingByChar[selectedId] || 0;
  const aiBusyHere = pendingForSelected > 0;

  const albumOwnedSetIds = useMemo(
    () => new Set((session?.sets || []).map((id) => String(id).trim()).filter(Boolean)),
    [session?.sets]
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, chatLocked, selectedId, pendingForSelected]);

  const persistCharMessages = useCallback(
    (characterId, list) => {
      saveChatMessages(playerId, characterId, list);
    },
    [playerId]
  );

  const applyRelationshipDelta = useCallback(
    async (characterId, delta) => {
      const d = Number(delta) || 0;
      if (!d || !playerId || !session) return;
      const { characters: chars, sets: mergedSets, prevRp, nextRp, newlyUnlockedIds } =
        applyRpDeltaToCharactersAndSets(
          session.characters || [],
          session.sets || [],
          characterId,
          d,
          setsCatalog || []
        );
      try {
        const res = await patchUserState({ playerId, characters: chars, sets: mergedSets });
        if (res?.userState) onUserStateUpdated(res.userState);
        const detail = buildRelationshipRewardDispatchDetail(
          prevRp,
          nextRp,
          gameSettings,
          newlyUnlockedIds,
          setsCatalog || []
        );
        window.dispatchEvent(new CustomEvent('game-flow-relationship-up', { detail }));
      } catch (e) {
        console.error(e);
      }
    },
    [playerId, session, onUserStateUpdated, gameSettings, setsCatalog]
  );

  const runGrokTurn = useCallback(
    async ({
      targetCharacterId,
      targetCharacterDoc,
      targetDisplayName,
      historyLines,
      messageText,
      selectedGift,
      optimisticUserMsg,
      sessionFollowUp = null
    }) => {
      if (!liveAi || !targetCharacterId || !targetCharacterDoc) return;
      const tid = String(targetCharacterId);
      onPendingAiDelta?.(1);
      setPendingByChar((p) => ({ ...p, [tid]: (p[tid] || 0) + 1 }));

      const appendToChar = (updater) => {
        const apply = (prevMap) => {
          const cur = prevMap[tid] ?? loadChatMessages(playerId, tid);
          const next = typeof updater === 'function' ? updater(cur) : updater;
          persistCharMessages(tid, next);
          return { ...prevMap, [tid]: next };
        };
        if (mountedRef.current) {
          setMessagesByChar(apply);
        } else {
          const cur = loadChatMessages(playerId, tid);
          const next = typeof updater === 'function' ? updater(cur) : updater;
          persistCharMessages(tid, next);
        }
      };

      if (optimisticUserMsg) {
        appendToChar((c) => [...c, optimisticUserMsg]);
      }

      try {
        const body = buildGrokChatRequestBody({
          playerId,
          playerName: playerNickname.trim(),
          characterId: tid,
          characterDoc: targetCharacterDoc,
          historyLines,
          messageText,
          selectedGift,
          gameSettings,
          session,
          kinksCatalog,
          sessionFollowUp
        });
        const data = await postGrokRequest(body);
        const reply = typeof data.message === 'string' ? data.message.trim() : '';
        const suggestedRaw = String(data.suggested_set_id || '').trim();
        const albumRow = albumRowFromSetCatalog(setsCatalog, suggestedRaw, tid);
        const ownedNow = new Set(
          (sessionRef.current?.sets || []).map((id) => String(id).trim()).filter(Boolean)
        );
        const alreadyOwnsSuggested = albumRow && ownedNow.has(albumRow.setId);

        const charName = targetCharacterDoc.name || targetDisplayName || 'Character';
        const aiMsg = {
          id: `ai-${Date.now()}-${tid}`,
          role: 'character',
          type: 'text',
          text: reply || '…'
        };

        const viewingThisThread =
          chatUiVisible && String(viewedCharacterId || '') === tid;
        if (!viewingThisThread) {
          const replyShown = replacePlayerNamePlaceholder(reply, playerNickname.trim());
          let preview =
            replyShown.length > 160 ? `${replyShown.slice(0, 157)}…` : replyShown || '…';
          if (albumRow && !alreadyOwnsSuggested) {
            const tail = `📷 ${albumRow.title}`;
            preview =
              preview && preview !== '…'
                ? `${preview}\n${tail}`
                : tail;
          }
          onChatReplyNotification?.({
            characterId: tid,
            characterName: charName,
            preview
          });
        }

        appendToChar((c) => {
          const next = [...c, aiMsg];
          if (albumRow && !alreadyOwnsSuggested) {
            next.push({
              id: `album-${Date.now()}-${tid}-${albumRow.setId}`,
              role: 'character',
              type: 'album',
              setId: albumRow.setId,
              title: albumRow.title,
              photoCount: albumRow.photoCount,
              coverImageUrl: albumRow.coverImageUrl,
              price: albumRow.price
            });
          }
          return next;
        });

        const rd = Number(data.relationship_delta);
        if (Number.isFinite(rd) && rd !== 0) {
          await applyRelationshipDelta(tid, rd);
        }
      } catch (e) {
        console.error(e);
        const fix = (c) => {
          let next = c;
          if (
            optimisticUserMsg &&
            next.length > 0 &&
            next[next.length - 1]?.id === optimisticUserMsg.id
          ) {
            next = next.slice(0, -1);
          }
          return [
            ...next,
            {
              id: `err-${Date.now()}`,
              role: 'system',
              type: 'error',
              text: e?.message || 'Could not reach AI. Try again.'
            }
          ];
        };
        appendToChar(fix);
      } finally {
        onPendingAiDelta?.(-1);
        setPendingByChar((p) => {
          const n = Math.max(0, (p[tid] || 0) - 1);
          const next = { ...p };
          if (n === 0) delete next[tid];
          else next[tid] = n;
          return next;
        });
      }
    },
    [
      liveAi,
      playerId,
      playerNickname,
      gameSettings,
      session,
      kinksCatalog,
      chatUiVisible,
      viewedCharacterId,
      onChatReplyNotification,
      onPendingAiDelta,
      applyRelationshipDelta,
      persistCharMessages,
      setsCatalog
    ]
  );

  useEffect(() => {
    if (!sessionFollowUpRequest || !liveAi) return undefined;
    const req = sessionFollowUpRequest;
    const seq = Number(req.seq);
    const hasSeq = Number.isFinite(seq) && seq > 0;
    if (hasSeq && seq <= lastHandledFollowUpSeqRef.current) {
      onSessionFollowUpConsumed?.();
      return undefined;
    }

    const tid = String(req.characterId || '').trim();
    if (!tid) {
      onSessionFollowUpConsumed?.();
      return undefined;
    }
    const doc = getCharDoc(charactersCatalog, tid);
    if (!doc) {
      onSessionFollowUpConsumed?.();
      return undefined;
    }

    let fu = null;
    if (req.kind === 'after_control') {
      fu = {
        afterControlSession: true,
        sessionHistory: String(req.sessionHistory || '')
      };
    } else if (req.kind === 'after_joi') {
      fu = {
        afterJoiSession: true,
        completedTasks: String(req.completedTasks || ''),
        refusedTasks: String(req.refusedTasks || '')
      };
    } else {
      onSessionFollowUpConsumed?.();
      return undefined;
    }

    if (hasSeq) {
      lastHandledFollowUpSeqRef.current = seq;
    }

    /*
     * Сбрасываем запрос в родителе сразу, до await: иначе каждый рендер давал новый
     * onSessionFollowUpConsumed (или новый runGrokTurn из deps) → эффект снова с тем же
     * request → сотни повторных вызовов Grok.
     */
    onSessionFollowUpConsumed?.();

    const nick = playerNickname.trim();
    const prior = loadChatMessages(playerId, tid);
    const historyLines = messagesToHistoryLines(prior, nick, doc.name);

    void (async () => {
      try {
        await runGrokTurn({
          targetCharacterId: tid,
          targetCharacterDoc: doc,
          targetDisplayName: doc.name,
          historyLines,
          messageText: '',
          selectedGift: null,
          optimisticUserMsg: undefined,
          sessionFollowUp: fu
        });
      } catch {
        /* runGrokTurn уже логирует / пишет error в чат */
      }
    })();

    return undefined;
  }, [
    sessionFollowUpRequest,
    liveAi,
    playerId,
    playerNickname,
    charactersCatalog,
    runGrokTurn,
    onSessionFollowUpConsumed
  ]);

  const send = () => {
    if (chatLocked || aiBusyHere) return;
    const text = draft.trim();
    if (!text) return;

    if (!gameMode) {
      setFixtureMessages((prev) => [...prev, { id: `preview-${Date.now()}`, role: 'user', type: 'text', text }]);
      setDraft('');
      return;
    }

    if (!liveAi || !characterDoc || !selectedId) {
      setDraft('');
      return;
    }

    const nick = playerNickname.trim();
    const prior = messagesByChar[selectedId] ?? loadChatMessages(playerId, selectedId);
    const historyLines = messagesToHistoryLines(prior, nick, characterDoc.name);
    const optimisticUserMsg = {
      id: `u-${Date.now()}`,
      role: 'user',
      type: 'text',
      text
    };
    setDraft('');
    void runGrokTurn({
      targetCharacterId: selectedId,
      targetCharacterDoc: characterDoc,
      targetDisplayName: selectedGame?.name,
      historyLines,
      messageText: text,
      selectedGift: null,
      optimisticUserMsg
    });
  };

  const handlePickGift = async (gift) => {
    if (chatLocked || aiBusyHere || !liveAi || !characterDoc || !selectedId || !playerId) return;
    const price = Number(gift.price) || 0;
    const bal = Number(session?.tokens) || 0;
    if (price > bal) {
      onInsufficientTokens?.();
      return;
    }
    setGiftPickerOpen(false);
    const nick = playerNickname.trim();
    const prior = messagesByChar[selectedId] ?? loadChatMessages(playerId, selectedId);
    const historyLines = messagesToHistoryLines(prior, nick, characterDoc.name);
    const optimisticUserMsg = {
      id: `gift-${Date.now()}`,
      role: 'user',
      type: 'text',
      text: `Gift: ${gift.name}`
    };
    if (price > 0) {
      try {
        const res = await patchUserState({ playerId, tokens: bal - price });
        if (res?.userState) onUserStateUpdated(res.userState);
      } catch (e) {
        console.error(e);
        return;
      }
    }
    await runGrokTurn({
      targetCharacterId: selectedId,
      targetCharacterDoc: characterDoc,
      targetDisplayName: selectedGame?.name,
      historyLines,
      messageText: '🎁',
      selectedGift: { name: gift.name, instruction: gift.instruction || '' },
      optimisticUserMsg
    });
  };

  const sidebarContacts = gameMode ? gameContacts : fixtureContacts;
  const activeId = gameMode ? String(selectedGame?.id ?? '') : '1';
  const showGameHeader = gameMode && typeof onClose === 'function' && selectedGame && chatUiVisible;

  const sidebarColWidth =
    'w-[min(32vw,13rem)] min-w-[10.5rem] max-w-[13rem] shrink-0 border-r border-[rgba(237,232,229,0.14)]';

  const showFixtureThread = !gameMode;

  return (
    <div
      className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-game-surface/40 ${
        embedded ? 'h-full rounded-none' : 'h-full rounded-xl border border-white/10'
      }`}
    >
      <div className="flex min-h-[44px] shrink-0 border-b border-[rgba(237,232,229,0.14)]">
        <div
          className={`flex items-center bg-game-tint px-3 text-[10px] font-semibold uppercase tracking-wide text-game-muted ${sidebarColWidth}`}
        >
          Contacts
        </div>
        <div className="relative flex min-h-[44px] min-w-0 flex-1 items-center justify-center bg-game-chatHeader/90 px-10 py-2 pr-12 backdrop-blur-sm sm:px-12 sm:pr-14">
          {showGameHeader ? (
            <>
              <span className="min-w-0 truncate text-center text-sm font-semibold text-game-text">
                {selectedGame.name}
              </span>
              {(() => {
                const row = (session?.characters || []).find((c) => String(c.characterId) === String(selectedId));
                const rp = Number(row?.relationship) || 0;
                const tier = relationshipTierIndex(rp, gameSettings);
                const milestone = nextRpMilestone(rp, tier, gameSettings);
                return (
                  <span className="ml-2 flex items-center gap-1">
                    <RelationshipBadge inline rpCurrent={rp} rpNext={Math.max(rp + 1, milestone)} tierLabel={relationshipTierLabel(tier)} />
                    <RpDeltaFloat delta={chatRpDelta.delta} seq={chatRpDelta.seq} />
                  </span>
                );
              })()}
              <div className="absolute right-2 top-1/2 z-[1] -translate-y-1/2 sm:right-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-xl leading-none text-game-muted transition-colors hover:bg-white/10 hover:text-game-text"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-row overflow-hidden">
        <aside className={`flex min-h-0 flex-col bg-game-tint ${sidebarColWidth}`}>
          <div className="scrollbar-none flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden p-2">
            {sidebarContacts.map((c, i) => {
              const cid = c.id;
              const isActive = gameMode ? String(cid) === String(activeId) : c.active;
              const pend = gameMode && liveAi ? pendingByChar[String(cid)] || 0 : 0;
              return (
                <motion.button
                  key={cid}
                  type="button"
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={staggerEntrance(i, 0.06, 0)}
                  onClick={() => {
                    if (gameMode && onGameSelectContact) onGameSelectContact(String(cid));
                  }}
                  className={`relative flex w-full min-w-0 items-center gap-2 rounded-lg px-2 py-2 text-left text-xs transition ${
                    isActive
                      ? 'bg-game-accent/20 text-game-text ring-1 ring-game-accent/40'
                      : 'text-game-body hover:bg-white/5'
                  }`}
                >
                  {pend > 0 ? (
                    <span
                      className="absolute right-1 top-1 flex h-2 w-2 rounded-full bg-game-accent shadow-sm"
                      aria-hidden
                    />
                  ) : null}
                  <span className="flex h-9 w-9 shrink-0 overflow-hidden rounded-full bg-game-surface text-sm ring-1 ring-white/10">
                    {c.avatarUrl ? (
                      <img src={c.avatarUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center">{c.name[0]}</span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-game-text">{c.name}</span>
                    <span className="block truncate text-[10px] text-game-muted">
                      {gameMode ? c.preview || '—' : c.preview}
                    </span>
                  </span>
                </motion.button>
              );
            })}
          </div>
        </aside>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-game-page">
          <div
            ref={scrollRef}
            className="scrollbar-none min-h-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden p-3"
          >
            {gameMode && chatLocked && selectedGame ? (
              <div className="flex justify-center px-3 pt-5">
                <div className="max-w-md rounded-xl border-2 border-dashed border-[rgba(237,232,229,0.5)] bg-transparent px-4 py-5 text-center">
                  <p className="text-sm leading-relaxed text-game-body">
                    Complete your first session with{' '}
                    <span className="font-medium text-game-text">{selectedGame.name}</span> before you can chat
                    or send gifts.
                  </p>
                </div>
              </div>
            ) : gameMode && !chatLocked && liveAi && !characterDoc ? (
              <p className="px-2 pt-6 text-center text-sm text-game-muted">Character data not loaded.</p>
            ) : gameMode && !chatLocked && liveAi && messages.length === 0 && !aiBusyHere ? (
              <p className="px-2 pt-6 text-center text-sm text-game-muted">
                Say hello — replies stay in this character&apos;s thread even if you switch away.
                {characterDoc?.preferenceDominant ? ' Your kinks from profile are sent for Dominant characters.' : ''}
              </p>
            ) : showFixtureThread ? (
              fixtureMessages.map((msg) => (
                <motion.div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                  <MessageBubble msg={msg} playerNickname={playerNickname} />
                </motion.div>
              ))
            ) : (
              <>
                {messages.map((msg) => (
                  <motion.div
                    key={msg.id}
                    className={`flex ${
                      msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'
                    }`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                  >
                    <MessageBubble
                      msg={msg}
                      playerNickname={playerNickname}
                      albumOwnedSetIds={albumOwnedSetIds}
                      onAlbumUnlock={onChatAlbumUnlock}
                      onAlbumOpen={onChatAlbumOpen}
                    />
                  </motion.div>
                ))}
                {aiBusyHere ? (
                  <p className="px-2 text-center text-xs text-game-muted">Writing…</p>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      <div
        className={`flex shrink-0 border-t border-[rgba(237,232,229,0.14)] bg-game-chat-input ${
          chatLocked ? 'pointer-events-none opacity-45' : ''
        }`}
      >
        <div className={`flex shrink-0 items-stretch p-2 ${sidebarColWidth}`}>
          <motion.button
            type="button"
            transition={snappyTween}
            whileHover={{ scale: chatLocked ? 1 : 1.02 }}
            whileTap={{ scale: chatLocked ? 1 : 0.98 }}
            disabled={chatLocked || !liveAi || aiBusyHere}
            onClick={() => setGiftPickerOpen(true)}
            className="flex min-h-[44px] w-full min-w-0 flex-col items-center justify-center gap-1 rounded-lg border border-white/12 bg-game-surface/85 px-2 py-2 text-[11px] font-semibold leading-tight text-game-text shadow-sm transition-colors hover:border-game-accent/35 hover:bg-game-surface disabled:opacity-40 sm:flex-row sm:gap-2 sm:px-2.5 sm:text-xs"
            aria-label="Send a gift"
          >
            <span className="text-lg leading-none sm:text-base" aria-hidden>
              🎁
            </span>
            <span className="text-center">Send gift</span>
          </motion.button>
        </div>
        <div className="min-w-0 flex-1 p-2">
          <div className="flex items-end gap-2">
            <textarea
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (chatLocked || aiBusyHere) return;
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              readOnly={chatLocked || aiBusyHere}
              placeholder={
                chatLocked ? 'Chat locked' : aiBusyHere ? 'Wait…' : 'Type a message…'
              }
              className="min-h-[40px] flex-1 resize-none rounded-xl border border-white/10 bg-game-surface/80 px-3 py-2 text-sm text-game-text placeholder:text-game-muted focus:border-game-accent/40 focus:outline-none focus:ring-1 focus:ring-game-accent/30 read-only:cursor-not-allowed"
              aria-label="Message"
            />
            <motion.button
              type="button"
              transition={snappyTween}
              whileHover={{ scale: chatLocked || aiBusyHere ? 1 : 1.03 }}
              whileTap={{ scale: chatLocked || aiBusyHere ? 1 : 0.97 }}
              disabled={chatLocked || aiBusyHere}
              className="shrink-0 rounded-xl bg-game-accent px-4 py-2 text-sm font-semibold text-game-accentText disabled:opacity-40"
              onClick={send}
            >
              Send
            </motion.button>
          </div>
        </div>
      </div>

      {giftPickerOpen && liveAi ? (
        <div className="pointer-events-auto fixed inset-0 z-[75]">
          <GamePreviewShell title="Gifts" onClose={() => setGiftPickerOpen(false)} overlayZClass="z-[75]">
            <div className="max-h-[min(70vh,420px)] space-y-2 overflow-y-auto p-3 sm:p-4">
              {giftCatalog == null ? (
                <p className="py-8 text-center text-sm text-game-muted">Loading gifts…</p>
              ) : giftCatalog.length === 0 ? (
                <p className="text-center text-sm text-game-muted">No gifts in catalog.</p>
              ) : (
                giftCatalog.map((g) => {
                  const price = Number(g.price) || 0;
                  const icon = String(g.icon || '').trim();
                  return (
                    <motion.button
                      key={g.id}
                      type="button"
                      whileTap={{ scale: 0.99 }}
                      disabled={aiBusyHere}
                      className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-game-surface/70 px-3 py-2.5 text-left disabled:opacity-50"
                      onClick={() => handlePickGift(g)}
                    >
                      {giftIconIsImageUrl(icon) ? (
                        <img src={icon} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover" />
                      ) : (
                        <span className="text-2xl">{icon || '🎁'}</span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-game-text">{g.name || 'Gift'}</p>
                        <p className="text-xs text-game-accent">{price} ◆</p>
                      </div>
                    </motion.button>
                  );
                })
              )}
              <motion.button
                type="button"
                whileTap={{ scale: 0.98 }}
                className="w-full rounded-xl py-2 text-sm text-game-muted"
                onClick={() => setGiftPickerOpen(false)}
              >
                Close
              </motion.button>
            </div>
          </GamePreviewShell>
        </div>
      ) : null}
    </div>
  );
}
